import AppTabsLinks from "@/components/shared/appTabsLinks";
import React, { useEffect, useState, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useSearchParams } from "next/navigation";
import { Input } from "@/components/shadcn/input";
import { Button } from "@/components/shadcn/button";

interface TabsData {
  key: string;
  name: string;
  link: string;
  external: boolean;
}

interface TabsSectionProps {
  isAdmin: boolean;
  showFixedTabs?: boolean;
}

const TabsSection = ({ isAdmin = false, showFixedTabs = false }: TabsSectionProps) => {
  const [activeTab, setActiveTab] = useState<string>("");
  const pathname = usePathname() ?? "";
  const router = useRouter();

  const submenuRef = useRef<HTMLDivElement>(null);
  const [isSticky, setSticky] = useState(false);
  const searchParams = useSearchParams();
  const [inputValue, setInputValue] = useState("");

  const learnTabs: TabsData[] = [
    { key: "courses", name: "Courses", link: "/learn/courses", external: false },
    ...(isAdmin
      ? [{ key: "trainers", name: "Trainers", link: "/learn/trainers", external: false }]
      : []),
    {
      key: "calender",
      name: "Events calendar",
      link: "https://www.proz.com/events_calendar",
      external: true,
    },
    {
      key: "library",
      name: "Video library",
      link: "https://videos.proz.com/",
      external: true,
    },
    { key: "dashboard", name: "My dashboard", link: "/learn/dashboard", external: false },
  ];

  useEffect(() => {
    if (pathname) {
      const active = learnTabs.find((tab) => pathname.includes(tab.link));
      setActiveTab(active ? active.link : "");
    }
  }, [pathname, learnTabs]);

  useEffect(() => {
    const initialOffset = submenuRef.current?.offsetTop ?? 0;

    const handleScroll = () => {
      if (window.scrollY >= initialOffset - 60) {
        setSticky(true);
      } else {
        setSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearch = () => {
    const searchButton = document.getElementById("searchButton");
    searchButton?.classList.add("animate-pulse");
    searchButton?.classList.add("cursor-wait");
    router.push(`/learn/courses?search=${encodeURIComponent(inputValue)}`);
    const searchValue = searchParams?.get("search") || "";
    if (searchValue === inputValue) {
      searchButton?.classList.remove("animate-pulse");
      searchButton?.classList.remove("cursor-wait");
    }
  };

  // Use showFixedTabs to control stickiness if needed
  const shouldShowSticky = showFixedTabs ? true : isSticky;

  return (
    <div className="bg-transparent rounded-t-3xl">
      <div className="pb-0 pt-4 md:pt-8">
        <div
          ref={submenuRef}
          className={`transition-all w-full ${
            shouldShowSticky
              ? "fixed left-0 bg-white dark:bg-gray-900 top-[60px] lg:top-[75px] shadow-md px-2 lg:px-[100px] py-2 z-10"
              : "relative"
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            {/* Tabs */}
            <div
              className={`flex overflow-x-auto lg:overflow-visible ${shouldShowSticky ? "justify-center lg:justify-start" : ""}`}
            >
              {learnTabs.map((item, index) => (
                <AppTabsLinks
                  key={`${index}-${pathname}`}
                  index={index}
                  rounded={shouldShowSticky}
                  tab={item}
                  isActive={activeTab === item.link}
                />
              ))}
            </div>

            {/* Search bar */}
            {shouldShowSticky && (
              <div className="flex gap-2 items-center justify-center w-full lg:w-auto">
                <Input
                  placeholder="What do you want to learn today?"
                  type="text"
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  value={inputValue}
                  className="flex-1 lg:w-80 max-w-[400px] text-xs lg:text-sm"
                />
                <Button
                  className="cursor-pointer"
                  size="default"
                  id="searchButton"
                  onClick={handleSearch}
                >
                  Search
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabsSection;
